
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BudgetBucket, SubCategory } from "../../types/budget.types";
import { Calendar, DollarSign, TrendingUp, Target, FileText } from "lucide-react";

interface SubcategoryFormData {
  name: string;
  budget: string;
  expRomi: string;
  kpi: string;
  description: string;
  expCompletion: string;
}

interface SubcategoryDialogsProps {
  selectedBucket: BudgetBucket | null;
  selectedSubcategory: SubCategory | null;
  formData: SubcategoryFormData;
  isAddDialogOpen: boolean;
  isEditDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  isDetailsDialogOpen: boolean;
  isAlertDialogOpen: boolean;
  alertMessage: string;
  handleFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleAddSubcategory: () => Promise<void>;
  handleUpdateSubcategory: () => Promise<void>;
  handleDeleteSubcategory: () => Promise<void>;
  setIsAddDialogOpen: (isOpen: boolean) => void;
  setIsEditDialogOpen: (isOpen: boolean) => void;
  setIsDeleteDialogOpen: (isOpen: boolean) => void;
  setIsDetailsDialogOpen: (isOpen: boolean) => void;
  setIsAlertDialogOpen: (isOpen: boolean) => void;
  openEditDialog: (bucketId: string, subcategory: SubCategory) => void;
}

export const SubcategoryDialogs: React.FC<SubcategoryDialogsProps> = ({
  selectedBucket,
  selectedSubcategory,
  formData,
  isAddDialogOpen,
  isEditDialogOpen,
  isDeleteDialogOpen,
  isDetailsDialogOpen,
  isAlertDialogOpen,
  alertMessage,
  handleFormChange,
  handleAddSubcategory,
  handleUpdateSubcategory,
  handleDeleteSubcategory,
  setIsAddDialogOpen,
  setIsEditDialogOpen,
  setIsDeleteDialogOpen,
  setIsDetailsDialogOpen,
  setIsAlertDialogOpen,
  openEditDialog
}) => {
  const [isExceedingBudget, setIsExceedingBudget] = useState(false);

  // Check budget value whenever it changes
  useEffect(() => {
    if (selectedBucket && formData.budget) {
      const budgetValue = parseInt(formData.budget.replace(/,/g, ''));
      setIsExceedingBudget(
        !isNaN(budgetValue) && budgetValue > selectedBucket.available
      );
    } else {
      setIsExceedingBudget(false);
    }
  }, [formData.budget, selectedBucket]);

  return (
    <>
      {/* Add Subcategory Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white text-black">
          <DialogHeader>
            <DialogTitle>Add New Subcategory</DialogTitle>
            <DialogDescription className="text-black">
              Create a new subcategory within the selected budget bucket
            </DialogDescription>
          </DialogHeader>
          {selectedBucket && <div className="bg-primary/10 p-3 rounded-md mb-3">
            <div className="flex justify-between text-black">
              <span className="font-medium">Total Budget:</span>
              <span>${selectedBucket.budget.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-black">
              <span className="font-medium">Available Budget:</span>
              <span className="font-bold">${selectedBucket.available.toLocaleString()}</span>
            </div>
          </div>}
          {isExceedingBudget && (
            <p className="text-red-500 text-sm mb-2">Value cannot exceed the available budget</p>
          )}
          <div className="grid gap-4 py-4 text-black">
            <div className="grid grid-cols-4 items-center gap-4 text-black">
              <Label htmlFor="name" className="text-right text-black">
                Name
              </Label>
              <Input id="name" name="name" placeholder="e.g., Press Releases" className="col-span-3 bg-white text-black" value={formData.name} onChange={handleFormChange} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4 text-black">
              <Label htmlFor="budget" className="text-right text-black">
                Budget ($)
              </Label>
              <Input 
                id="budget" 
                name="budget" 
                placeholder="e.g., 150,000" 
                value={formData.budget} 
                onChange={handleFormChange} 
                className={`col-span-3 rounded bg-white text-black ${isExceedingBudget ? 'border-red-500' : ''}`}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4 text-black">
              <Label htmlFor="expRomi" className="text-right text-black">
                Expected ROMI
              </Label>
              <Input id="expRomi" name="expRomi" placeholder="e.g., 3.5x" value={formData.expRomi} onChange={handleFormChange} className="col-span-3 rounded bg-white text-black" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4 text-black">
              <Label htmlFor="kpi" className="text-right text-black">
                KPI
              </Label>
              <Input id="kpi" name="kpi" placeholder="e.g., 5000 leads" value={formData.kpi} onChange={handleFormChange} className="col-span-3 rounded bg-white text-black" />
            </div>
            <div className="grid grid-cols-4 items-center gap-4 text-black">
              <Label htmlFor="expCompletion" className="text-right text-black">
                Expected Completion
              </Label>
              <Input id="expCompletion" name="expCompletion" type="date" className="col-span-3 bg-white text-black" value={formData.expCompletion} onChange={handleFormChange} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4 text-black">
              <Label htmlFor="description" className="text-right text-black">
                Description
              </Label>
              <Textarea id="description" name="description" placeholder="Add details about this subcategory" value={formData.description} onChange={handleFormChange} className="col-span-3 rounded bg-white text-black" />
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-white text-black" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSubcategory} disabled={isExceedingBudget}>
              Add Subcategory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Subcategory Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Subcategory</DialogTitle>
            <DialogDescription>
              Update the details for this subcategory
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input id="name" name="name" className="col-span-3 text-black" value={formData.name} onChange={handleFormChange} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="budget" className="text-right">
                Budget ($)
              </Label>
              <Input id="budget" name="budget" className="col-span-3 text-black" value={formData.budget} onChange={handleFormChange} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="expRomi" className="text-right">
                Expected ROMI
              </Label>
              <Input id="expRomi" name="expRomi" className="col-span-3 text-black" value={formData.expRomi} onChange={handleFormChange} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="kpi" className="text-right">
                KPI
              </Label>
              <Input id="kpi" name="kpi" className="col-span-3 text-black" value={formData.kpi} onChange={handleFormChange} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="expCompletion" className="text-right">
                Expected Completion
              </Label>
              <Input id="expCompletion" name="expCompletion" type="date" className="col-span-3 text-black" value={formData.expCompletion} onChange={handleFormChange} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea id="description" name="description" className="col-span-3 text-black" value={formData.description} onChange={handleFormChange} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSubcategory}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Subcategory Dialog - Confirmation Popup */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Confirm Delete Subcategory</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this subcategory? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-2">You are about to delete:</p>
            <div className="p-3 bg-secondary/10 rounded-md">
              <p className="font-medium">{selectedSubcategory?.name}</p>
              <p className="text-sm text-muted-foreground">
                Budget: ${selectedSubcategory?.budget.toLocaleString()}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" variant="destructive" onClick={handleDeleteSubcategory}>
              Delete Subcategory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Subcategory Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Subcategory Details</DialogTitle>
            <DialogDescription>
              Detailed information about this subcategory
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedSubcategory && <>
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">{selectedSubcategory.name}</h3>
                  <div className="bg-primary/20 text-primary font-semibold px-3 py-1 rounded-full text-sm">
                    ${selectedSubcategory.budget.toLocaleString()}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-xs text-muted-foreground">Expected ROMI</p>
                      <p className="font-medium">{selectedSubcategory.expRomi || "-"}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-xs text-muted-foreground">KPI</p>
                      <p className="font-medium">{selectedSubcategory.kpi || "-"}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-xs text-muted-foreground">Expected Completion</p>
                      <p className="font-medium">
                        {selectedSubcategory.expCompletion ? new Date(selectedSubcategory.expCompletion).toLocaleDateString() : "-"}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-xs text-muted-foreground">Budget</p>
                      <p className="font-medium">${selectedSubcategory.budget.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                
                {selectedSubcategory.description && <div className="mt-4 pt-4 border-t border-secondary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <p className="text-sm text-muted-foreground">Description</p>
                    </div>
                    <p className="text-sm">{selectedSubcategory.description}</p>
                  </div>}
              </>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setIsDetailsDialogOpen(false);
              if (selectedBucket && selectedSubcategory) {
                openEditDialog(selectedBucket.id, selectedSubcategory);
              }
            }}>
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Alert Dialog for Budget Validation */}
      <AlertDialog open={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Budget Limit Exceeded</AlertDialogTitle>
            <AlertDialogDescription>
              {alertMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
